import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import toSafeInteger from 'lodash/toSafeInteger';
import qs from 'query-string';
import { useRouter } from 'react-resource-router';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { emphasizeAndEscapeText } from '@atlassian/help-center-common-component/result-base';
import type { articlesFragment$data } from '../articles/__generated__/articlesFragment.graphql';

export const getExternalResourcesCount = (resources: articlesFragment$data['articles'] | undefined) =>
    resources?.filter((resource) =>
        resource?.__typename === 'HelpObjectStoreSearchResult' ? resource.isExternal : false
    ).length;

export const getSelectedPortalsFromQueryParams = (queryStringParams: { portalIds: string }) => {
    const { portalIds: selectedPortalIds } = queryStringParams;

    if (!selectedPortalIds) {
        return [];
    }

    return Array.from(selectedPortalIds);
};

export const formatText = (text: string = '', term: string = '') =>
    emphasizeAndEscapeText(text, term)
        .replace(/@@@hl@@@/g, '<strong>')
        .replace(/@@@endhl@@@/g, '</strong>');

export const useQueryParams = () => {
    const [{ location }, _] = useRouter();
    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const queryStringParams = qs.parse(location.search);
    // Suppressing existing violation. Please fix this.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return { ...queryStringParams, portalIds: getSelectedPortalsFromQueryParams(queryStringParams) };
};

export const isSinglePortalSearch = (portalId: string | number | undefined) =>
    portalId !== undefined && getPortalIdAsNumber(portalId) !== 0;

export const isValidSearchTerm = (term: string | undefined) => term && term.trim() !== '';

export const getPortalIdAsNumber = (portalId: string | number | null | undefined) => {
    return toSafeInteger(portalId);
};

export const getIdsArray = (ids: string[] | string | number | null | undefined) => {
    if (ids) {
        if (isNumber(ids) || isString(ids)) {
            return [ids.toString()];
        }

        return isArray(ids) ? ids : [];
    }
    return [];
};

export const sendSearchUIAnalyticEvents = (
    eventProperties: { externalResourcesCount?: number; resourceType: string; resourceCount: number },
    createAnalyticsEvent?: CreateUIAnalyticsEvent,
    portalId?: string
) => {
    createAnalyticsEvent &&
        createAnalyticsEvent({
            analyticsType: 'UI',
            action: 'rendered',
            actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
            ...eventProperties,
        }).fire();
};

export const sendSearchOperationalAnalyticEvents = (
    eventProperties: { action: string },
    createAnalyticsEvent?: CreateUIAnalyticsEvent,
    portalId?: string
) => {
    createAnalyticsEvent &&
        createAnalyticsEvent({
            analyticsType: 'OPERATIONAL',
            actionSubjectId: portalId ? 'singlePortal' : 'portalHomePage',
            ...eventProperties,
        }).fire();
};
